import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2 } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { z } from 'zod';

import { PersonalBackgroundFormSchema } from '@/components/forms/schemas/zee/PersonalBackgroundFormSchema';
import { IndustryBackgroundForm } from '@/components/forms/zee/background/ZeeIndustryBackgroundForm';
import { IndustryInterestForm } from '@/components/forms/zee/background/ZeeIndustryInterestForm';
import { PersonalBackgroundForm } from '@/components/forms/zee/background/ZeePersonalBackgroundForm';
import { Button } from '@/components/ui/button';
import { useCreateLeadBackground } from '@/hooks/leads/useCreateLeadBackground';
import { useGetLeadBackground } from '@/hooks/leads/useGetLeadBackground';
import { useUpdateLeadBackground } from '@/hooks/leads/useUpdateLeadBackground';
import { toast } from '@/hooks/ui/use-toast';
import useLeadStore from '@/stores/useLeadStore';

type LeadBackgroundFormValues = z.infer<typeof PersonalBackgroundFormSchema>;

const LeadBackgroundTab = () => {
  const { id: leadId } = useParams<{ id: string }>();
  const [industryInterests, setIndustryInterests] = useState<
    Record<string, number>
  >({});
  const [selectedIndustries, setSelectedIndustries] = useState<string[]>([]);

  const personalBackgroundForm = useForm<LeadBackgroundFormValues>({
    resolver: zodResolver(PersonalBackgroundFormSchema),
    defaultValues: {
      has_bankruptcy: '',
      has_criminal_conviction: '',
      has_pending_lawsuits: '',
      has_civil_judgments: '',
      affirmative_answers_explanation: null,
    },
  });

  const { candidateBackground, setCandidateBackground } = useLeadStore();

  const {
    result: candidateBackgroundResult,
    isError: isCandidateBackgroundError,
    isSuccess: isCandidateBackgroundSuccess,
    isLoading: isCandidateBackgroundLoading,
  } = useGetLeadBackground(leadId || '');

  const {
    mutate: createCandidateBackground,
    result: createCandidateBackgroundResult,
    isError: isCreateCandidateBackgroundError,
    isSuccess: isCreateCandidateBackgroundSuccess,
    isLoading: isCreateCandidateBackgroundLoading,
  } = useCreateLeadBackground();

  const {
    mutate: updateCandidateBackground,
    result: updateCandidateBackgroundResult,
    isError: isUpdateCandidateBackgroundError,
    isSuccess: isUpdateCandidateBackgroundSuccess,
    isLoading: isUpdateCandidateBackgroundLoading,
  } = useUpdateLeadBackground();

  const resetForms = useCallback(() => {
    personalBackgroundForm.reset({
      has_bankruptcy: candidateBackground?.has_bankruptcy || '',
      has_criminal_conviction:
        candidateBackground?.has_criminal_conviction || '',
      has_pending_lawsuits: candidateBackground?.has_pending_lawsuits || '',
      has_civil_judgments: candidateBackground?.has_civil_judgments || '',
      affirmative_answers_explanation:
        candidateBackground?.affirmative_answers_explanation || null,
    });

    setSelectedIndustries([
      ...(candidateBackground?.industry_background || []),
    ]);
    setIndustryInterests({
      ...(candidateBackground?.industry_interest || {}),
    });
  }, [candidateBackground, personalBackgroundForm]);

  const handleSubmit = async (): Promise<void> => {
    const isPersonalBackgroundFormValid =
      await personalBackgroundForm.trigger();

    if (isPersonalBackgroundFormValid) {
      const formValues = personalBackgroundForm.getValues();
      const submitPayload = {
        ...formValues,
        affirmative_answers_explanation:
          formValues.affirmative_answers_explanation || null,
        industry_background: selectedIndustries,
        industry_interest: industryInterests,
      };

      if (!candidateBackground) {
        createCandidateBackground(leadId || '', submitPayload);
      } else {
        updateCandidateBackground(leadId || '', submitPayload);
      }
    }
  };

  const renderEditButtons = () => {
    return (
      <div className="flex w-full justify-end">
        <div className="flex gap-1">
          <Button size="sm" className="w-full md:w-fit" onClick={handleSubmit}>
            Save Changes
          </Button>
        </div>
      </div>
    );
  };

  useEffect(() => {
    if (isCandidateBackgroundError) {
      toast({
        title: 'Something went wrong',
        description: 'Please try again',
        variant: 'destructive',
      });
    }
  }, [isCandidateBackgroundError]);

  useEffect(() => {
    if (isCandidateBackgroundSuccess && candidateBackgroundResult?.data) {
      if (candidateBackgroundResult?.data) {
        setCandidateBackground(candidateBackgroundResult?.data);
      }
      resetForms();
    }
  }, [
    candidateBackgroundResult,
    isCandidateBackgroundSuccess,
    resetForms,
    setCandidateBackground,
  ]);

  useEffect(() => {
    if (isUpdateCandidateBackgroundError) {
      const formValues = personalBackgroundForm.getValues();
      const submitPayload = {
        ...formValues,
        affirmative_answers_explanation:
          formValues.affirmative_answers_explanation || null,
        industry_background: selectedIndustries,
        industry_interest: industryInterests,
      };
      createCandidateBackground(leadId || '', submitPayload);
    }
  }, [
    isUpdateCandidateBackgroundError,
    personalBackgroundForm,
    selectedIndustries,
    industryInterests,
    createCandidateBackground,
    leadId,
  ]);

  useEffect(() => {
    if (isCreateCandidateBackgroundError) {
      toast({
        title: 'Something went wrong',
        description: 'Failed to save background information. Please try again.',
        variant: 'destructive',
      });
    }
  }, [isCreateCandidateBackgroundError]);

  useEffect(() => {
    if (
      isCreateCandidateBackgroundSuccess ||
      isUpdateCandidateBackgroundSuccess
    ) {
      if (createCandidateBackgroundResult?.data) {
        setCandidateBackground(createCandidateBackgroundResult?.data);
      }

      if (updateCandidateBackgroundResult?.data) {
        setCandidateBackground(updateCandidateBackgroundResult?.data);
      }

      resetForms();
    }
  }, [
    createCandidateBackgroundResult,
    updateCandidateBackgroundResult,
    isCreateCandidateBackgroundSuccess,
    isUpdateCandidateBackgroundSuccess,
    resetForms,
    setCandidateBackground,
  ]);

  if (isCandidateBackgroundLoading) {
    return (
      <div className="flex h-full w-full flex-1 items-center justify-center">
        <Loader2 className="size-4 animate-spin" />
      </div>
    );
  }

  return (
    <div className="relative space-y-1.5">
      {(isCreateCandidateBackgroundLoading ||
        isUpdateCandidateBackgroundLoading) && (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-gray-100/50 backdrop-blur-sm">
          <Loader2 className="size-4 animate-spin" />
        </div>
      )}

      <PersonalBackgroundForm form={personalBackgroundForm} />
      <IndustryBackgroundForm
        selectedIndustries={selectedIndustries}
        setSelectedIndustries={setSelectedIndustries}
      />
      <IndustryInterestForm
        industryInterests={industryInterests}
        setIndustryInterests={setIndustryInterests}
      />
      {renderEditButtons()}
    </div>
  );
};

export default LeadBackgroundTab;
